

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Chip,
  Stack,
  Divider,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { Download, DateRange } from "@mui/icons-material";
import { Pie, Bar, Line } from "react-chartjs-2";
import api from "../../utils/api";
import { processWorkOrderComparison, processWorkOrderTypeComparison } from "./utils";
import ExportTab from "./WorkOrdersExportPage";
// Charts
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

// PDF export
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const GROUP_NAME_BY_CODE = {
  "EH CH HILL": "Estate Homes",
  "EH EDGMT": "Estate Homes",
  SV: "Sommerset Villas",
  SC: "Southmont Cove",
  SW: "Sutton Walk",
  "VILLA HMS": "Villa Homes",
  WF: "Waterford",
  WW: "Wedgewood",
};

const norm = (v) => (v ?? "").toString().trim();
const canon = (v) => norm(v).replace(/\s+/g, " ").toUpperCase();
const isRenter = (v) => /\bRENTER\b/i.test(v);

const normalizeMembershipCode = (v) => {
  const c = canon(v);
  if (/^(SV|SC|SW|WF|WW)\b/.test(c)) {
    return c.match(/^(SV|SC|SW|WF|WW)/)[1];
  }
  return c;
};

export const groupNameFor = (raw) => {
  const c = normalizeMembershipCode(raw);
  return GROUP_NAME_BY_CODE[c] || norm(raw);
};

export const groupIdFor = (raw) => canon(groupNameFor(raw));


// Register once (no duplicates)
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);


const softColors = [
  "#A3E4D7", "#F9E79F", "#AED6F1", "#F5B7B1", "#D2B4DE",
  "#A9CCE3", "#FAD7A0", "#A2D9CE", "#F5CBA7", "#BB8FCE"
];

const presets = [
  { key: "30d", label: "Last 30 days", get: () => ({ start: offsetDays(30), end: todayISO() }) },
  { key: "90d", label: "Last 90 days", get: () => ({ start: offsetDays(90), end: todayISO() }) },
  { key: "mtd", label: "MTD", get: () => ({ start: firstOfMonthISO(), end: todayISO() }) },
  { key: "ytd", label: "YTD", get: () => ({ start: `${new Date().getFullYear()}-01-01`, end: todayISO() }) },
];

function todayISO() { return new Date().toISOString().slice(0, 10); }
function offsetDays(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0,10); }
function firstOfMonthISO(){ const d = new Date(); d.setDate(1); return d.toISOString().slice(0,10); }

function addDaysISO(dateStr, days) {
  if (!dateStr) return dateStr;
  const d = new Date(dateStr + "T00:00:00"); // ensure local midnight
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const GlassCard = ({ children, sx, ...props }) => (
  <Card
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 4,
      backdropFilter: "blur(8px)",
      background: (theme) => theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(255,255,255,0.8)",
      border: (theme) => `1px solid ${theme.palette.divider}`,
      boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
      ...sx,
    }}
    {...props}
  >
    {children}
  </Card>
);

const StatTile = ({ label, value, sublabel }) => (
  <GlassCard sx={{ textAlign: "center", py: 4 }}>
    <Typography variant="overline" sx={{ letterSpacing: 1.1 }}>{label}</Typography>
    <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
    {sublabel && (
      <Typography variant="caption" color="text.secondary">{sublabel}</Typography>
    )}
  </GlassCard>
);

const SectionHeader = ({ title, right }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
    {right}
  </Box>
);

// ====== Main component ======
const WorkOrdersTab = () => {
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [trendView, setTrendView] = useState("month");
  const [comparisonData, setComparisonData] = useState({ current: [], previous: [], labels: [] });
  const [typeComparisonData, setTypeComparisonData] = useState({ labels: [], current: [], previous: [] });
  const [activeTab, setActiveTab] = useState(0);

  // ===== Filters (status removed per request) =====
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedSubtype, setSelectedSubtype] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [includeMember, setIncludeMember] = useState(true);
  const [membershipType, setMembershipType] = useState("");
  const [membershipTypeOptions, setMembershipTypeOptions] = useState([]); 
  const [membershipTypeLoading, setMembershipTypeLoading] = useState(false); 
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [statuses, setStatuses] = useState([]); // retained in case other parts rely on it

  // Printable area (cards + charts)
  const overviewRef = useRef(null);

  useEffect(() => {
    fetchFilters();
    const { start, end } = presets.find(p => p.key === "90d").get();
    setStartDate(start); setEndDate(end);
  }, []);

  useEffect(() => {
    // Auto-fetch as inputs change
    const t = setTimeout(() => fetchData(true), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedType, selectedSubtype, selectedLocation, propertyId, includeMember, membershipType]);

  useEffect(() => {
    const { labels, current, previous } = processWorkOrderComparison(workOrders, "created_at", trendView);
    setComparisonData({ labels, current, previous });
  }, [trendView, workOrders]);

  useEffect(() => {
    const { labels, current, previous } = processWorkOrderTypeComparison(workOrders, "created_at", trendView);
    setTypeComparisonData({ labels, current, previous });
  }, [trendView, workOrders]);

  const fetchFilters = async () => {
    try {
      const [typesRes, subtypesRes, locationsRes, statusesRes] = await Promise.all([
        api.get("/workorder-types"),
        api.get("/workorder-subtypes"),
        api.get("/workorder-locations"),
        api.get("/statuses/workorders"),
      ]);
      setTypes(typesRes.data);
      setSubtypes(subtypesRes.data);
      setLocations(locationsRes.data);
      setStatuses(statusesRes.data);
    } catch (err) {
      console.error("Error fetching filter data:", err);
    }
  };



const buildMembershipOptionsFromRows = (rows) => {
  const groups = new Map(); // groupId -> {label, rawCodes[]}

  for (const o of rows || []) {
    const raw = o?.member?.ClubMembershipTypeCode;
    if (!raw || isRenter(raw)) continue;

    const id = groupIdFor(raw);   // normalized key (e.g. "ESTATE HOMES")
    const label = groupNameFor(raw); // display label (e.g. "Estate Homes")

    const entry = groups.get(id) || { value: id, label, rawCodes: [] };
    entry.rawCodes.push(raw.trim());
    groups.set(id, entry);
  }

  return Array.from(groups.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};

const fetchMembershipTypesFromReport = async () => {
  setMembershipTypeLoading(true);
  try {
    // Build params from current filters, but force includeMember=1 and DO NOT send club_membership_type_code
    const params = new URLSearchParams();
    if (startDate) params.append("start", startDate);
    if (endDate) params.append("end", addDaysISO(endDate, 1));
    if (selectedType) params.append("type_id", selectedType);
    if (selectedSubtype) params.append("subtype_id", selectedSubtype);
    if (selectedLocation) params.append("location_id", selectedLocation);
    if (propertyId.trim()) params.append("property_id", propertyId.trim());
    params.append("includeMember", "1"); // important for codes

    const { data } = await api.get(`/getWorkOrdersReport?${params.toString()}`);
    setMembershipTypeOptions(buildMembershipOptionsFromRows(data || []));
  } catch (e) {
    console.error("membership types (reports api) fetch error:", e);
    setMembershipTypeOptions([]); // graceful fallback
  } finally {
    setMembershipTypeLoading(false);
  }
};

// Recompute options whenever the displayed workOrders change and includeMember is ON
useEffect(() => {
  if (includeMember) {
    setMembershipTypeOptions(buildMembershipOptionsFromRows(workOrders));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [workOrders, includeMember]);

// When includeMember is OFF (or on first load), fetch options from the same reports API
useEffect(() => {
  // Trigger on filter changes that affect the available codes
  const t = setTimeout(() => {
    if (!includeMember) {
      fetchMembershipTypesFromReport();
    }
  }, 250);
  return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [includeMember, startDate, endDate, selectedType, selectedSubtype, selectedLocation, propertyId]);


  const fetchData = async (withFilters = false) => {
    setLoading(true);
    try {
      if (withFilters) {
        const params = new URLSearchParams();
        if (startDate) params.append("start", startDate);
        if (endDate) params.append("end", addDaysISO(endDate, 1));
        if (selectedType) params.append("type_id", selectedType);
        if (selectedSubtype) params.append("subtype_id", selectedSubtype);
        if (selectedLocation) params.append("location_id", selectedLocation);
        if (propertyId.trim()) params.append("property_id", propertyId.trim());
        if (includeMember) params.append("includeMember", "1");
        if (membershipType) {
   const opt = membershipTypeOptions.find(o => o.value === membershipType);    if (opt && opt.rawCodes?.length) {
    params.append("club_membership_type_code", opt.rawCodes.join(","));
   }
 }


        const { data } = await api.get(`/getWorkOrdersReport?${params.toString()}`);
        setWorkOrders(data || []);
      } else {
        const ordersRes = await api.get(`/getWorkOrders`);
        setWorkOrders(ordersRes.data || []);
      }
    } catch (error) {
      console.error("Error fetching work orders:", error);
    } finally {
      setLoading(false);
    }
  };


  



  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
    setSelectedSubtype("");
    setSelectedLocation("");
  };

  const handleSubtypeChange = (e) => {
    setSelectedSubtype(e.target.value);
    setSelectedLocation("");
  };

  // ===== Derived metrics =====
  const totalWorkOrders = workOrders.length;
  const completedWorkOrders = useMemo(() => workOrders.filter(o => (o.status||"").toLowerCase() === "resolved").length, [workOrders]);
  const openWorkOrders = totalWorkOrders - completedWorkOrders;

  const { avgCompletionDays, medianCompletionDays } = useMemo(() => {
    const completed = workOrders.filter(order => order.resolved_at && order.created_at);
    if (completed.length === 0) return { avgCompletionDays: 0, medianCompletionDays: 0 };
    const days = completed.map(order => {
      const created = new Date(order.created_at);
      const resolved = new Date(order.resolved_at);
      return (resolved - created) / (1000 * 60 * 60 * 24);
    }).sort((a,b)=>a-b);
    const avg = days.reduce((a,b)=>a+b,0)/days.length;
    const mid = Math.floor(days.length/2);
    const median = days.length%2===0 ? (days[mid-1]+days[mid])/2 : days[mid];
    return { avgCompletionDays: Number(avg.toFixed(1)), medianCompletionDays: Number(median.toFixed(1)) };
  }, [workOrders]);

  // ===== Charts (finely tuned for a softer, Apple-like feel) =====
  const basePlugins = {
    legend: { labels: { boxWidth: 12, usePointStyle: true } },
    tooltip: { cornerRadius: 10, titleColor: "#111", bodyColor: "#111" },
  };

  const statusCounts = useMemo(()=>workOrders.reduce((acc, order) => { const st = order.status || "Unknown"; acc[st] = (acc[st] || 0) + 1; return acc; }, {}),[workOrders]);
  const statusChartData = useMemo(()=>({ labels: Object.keys(statusCounts), datasets: [{ data: Object.values(statusCounts), backgroundColor: softColors, borderWidth: 0 }] }),[statusCounts]);

  const priorityCounts = useMemo(()=>workOrders.reduce((acc, order) => { const p = order.priority || "Unknown"; acc[p] = (acc[p] || 0) + 1; return acc; }, {}),[workOrders]);
  const priorityChartData = useMemo(()=>({ labels: Object.keys(priorityCounts), datasets: [{ data: Object.values(priorityCounts), backgroundColor: softColors, borderWidth: 0 }] }),[priorityCounts]);

 const membershipCounts = useMemo(() => {
   const acc = {};
   for (const o of workOrders) {
     const raw = o?.member?.ClubMembershipTypeCode;
     if (!raw || isRenter(raw)) continue; // skip blank or renter

     const label = groupNameFor(raw);
     acc[label] = (acc[label] || 0) + 1;
   }
   return acc;
 }, [workOrders]);




  const membershipChartData = useMemo(()=>({
    labels: Object.keys(membershipCounts),
    datasets: [{ label: "Work Orders", data: Object.values(membershipCounts), backgroundColor: "rgba(33,150,243,0.15)", borderColor: "rgba(33,150,243,0.7)", borderWidth: 1, borderRadius: 8, barThickness: 16 }],
  }),[membershipCounts]);

  const comparisonChartData = useMemo(()=>({
    labels: comparisonData.labels,
    datasets: [
      { label: `This ${trendView}`, data: comparisonData.current, borderColor: "rgba(33,150,243,1)", backgroundColor: "rgba(33,150,243,0.15)", fill: true, tension: 0.35, pointRadius: 2 },
      { label: `Last ${trendView}`, data: comparisonData.previous, borderColor: "rgba(244,67,54,1)", backgroundColor: "rgba(244,67,54,0.1)", fill: true, tension: 0.35, pointRadius: 2 },
    ]
  }),[comparisonData, trendView]);

  const typeComparisonChartData = useMemo(()=>({
    labels: typeComparisonData.labels,
    datasets: [
      { label: `This ${trendView}`, data: typeComparisonData.current, backgroundColor: "rgba(33,150,243,0.7)", borderRadius: 8, barThickness: 18 },
      { label: `Last ${trendView}`, data: typeComparisonData.previous, backgroundColor: "rgba(244,67,54,0.6)", borderRadius: 8, barThickness: 18 },
    ],
  }),[typeComparisonData, trendView]);

  // PDF export
  const handleExportOverviewToPDF = async () => {
    if (!overviewRef.current) return;

    const canvas = await html2canvas(overviewRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      windowWidth: document.documentElement.scrollWidth,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const filename = `WorkOrders_Overview_${new Date().toISOString().slice(0, 10)}.pdf`;
    pdf.save(filename);
  };

  const EmptyState = () => (
    <GlassCard sx={{ textAlign: "center", py: 8 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>No data to display</Typography>
      <Typography variant="body2" color="text.secondary">Try broadening your filters or selecting a different date range.</Typography>
    </GlassCard>
  );

  return (
    <Box sx={{ pb: 6 }}>
      {/* Hero Header */}
      <Box sx={{
        mb: 3,
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        background: (theme) => theme.palette.mode === "dark"
          ? "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))"
          : "linear-gradient(180deg, #f7f7fb, #ffffff)",
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}>
        <Box sx={{ display: "flex", alignItems: { xs: "start", md: "center" }, justifyContent: "space-between", gap: 2, flexDirection: { xs: "column", md: "row" }}}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -0.2 }}>🛠️ Work Orders</Typography>
            <Typography color="text.secondary">Operational overview, trends, and composition.</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Download />} onClick={handleExportOverviewToPDF}>Download PDF</Button>
          </Stack>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(e, v)=>setActiveTab(v)}
          textColor="primary"
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{
            mt: 2,
            
            "& .MuiTabs-flexContainer": {
              gap: 1,
              p: 0.5,
              borderRadius: 999,
              backgroundColor: (t)=> t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#f1f3f5",
            },
            "& .MuiTab-root": {
              minHeight: 36,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 600,
              px: 2.25,
            },
            "& .Mui-selected": {
              backgroundColor: (t)=> t.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#fff",
              boxShadow: (t)=> t.palette.mode === "dark" ? "inset 0 0 0 1px rgba(255,255,255,0.12)" : "0 1px 1px rgba(0,0,0,0.04)",
            }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Export" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <>
          {/* ===== Printable Content ===== */}
          <Box ref={overviewRef}>
            {/* KPI cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={3}><StatTile label="Total Work Orders" value={totalWorkOrders} /></Grid>
              <Grid item xs={12} md={3}><StatTile label="Resolved" value={completedWorkOrders} /></Grid>
              <Grid item xs={12} md={3}><StatTile label="Open" value={openWorkOrders} /></Grid>
              <Grid item xs={12} md={3}><StatTile label="Avg / Median Days" value={`${avgCompletionDays} / ${medianCompletionDays}`} sublabel="to resolve" /></Grid>
            </Grid>
 {/* ===== Filters (non-print controls) ===== */}
          <GlassCard sx={{ p: 2.5, mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>Filters</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Member/Property ID" value={propertyId} onChange={(e)=>setPropertyId(e.target.value)} />
              </Grid>
<Grid item xs={12} md={3}>
  <FormControl fullWidth>
    <InputLabel>ClubMembershipTypeCode</InputLabel>
    <Select
      value={membershipType}
      label="ClubMembershipTypeCode"
      onChange={(e)=>setMembershipType(e.target.value)}
      disabled={membershipTypeLoading}
    >
      <MenuItem value="">All</MenuItem>
      {membershipTypeOptions.map(opt => (
        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
      ))}
    </Select>
  </FormControl>
</Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select value={selectedType} label="Type" onChange={handleTypeChange}>
                    <MenuItem value="">All</MenuItem>
                    {types.map((t)=> <MenuItem key={t.id} value={t.id}>{t.type}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth disabled={!selectedType}>
                  <InputLabel>Subtype</InputLabel>
                  <Select value={selectedSubtype} label="Subtype" onChange={handleSubtypeChange}>
                    <MenuItem value="">All</MenuItem>
                    {subtypes.filter(s=> s.type_id === Number(selectedType)).map((s)=> <MenuItem key={s.id} value={s.id}>{s.subtype_name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth disabled={!selectedType}>
                  <InputLabel>Location</InputLabel>
                  <Select value={selectedLocation} label="Location" onChange={(e)=>setSelectedLocation(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    {locations.filter(l=> l.type_id === Number(selectedType)).map((l)=> <MenuItem key={l.id} value={l.id}>{l.location_name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField fullWidth type="date" label="Start" InputLabelProps={{shrink:true}} value={startDate} onChange={(e)=>setStartDate(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth type="date" label="End" InputLabelProps={{shrink:true}} value={endDate} onChange={(e)=>setEndDate(e.target.value)} />
              </Grid>

              {/* Presets as chips */}
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {presets.map(p=> {
                    const { start, end } = p.get();
                    const active = startDate===start && endDate===end;
                    return (
                      <Chip
                        key={p.key}
                        icon={<DateRange />}
                        size="small"
                        label={p.label}
                        color={active?"primary":"default"}
                        variant={active?"filled":"outlined"}
                        onClick={()=>{ setStartDate(start); setEndDate(end); }}
                      />
                    );
                  })}
                </Stack>
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControlLabel control={<Switch checked={includeMember} onChange={(e)=>setIncludeMember(e.target.checked)} />} label="Include member info" />
              </Grid>

              <Grid item xs={12} md={2}>
                <Button fullWidth variant="contained" onClick={()=>fetchData(true)}>Apply</Button>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button fullWidth variant="text" color="secondary" onClick={()=>{
                  setSelectedType(""); setSelectedSubtype(""); setSelectedLocation(""); setPropertyId(""); setMembershipType("");
                  const { start, end } = presets.find(p=>p.key==="90d").get(); setStartDate(start); setEndDate(end);
                }}>Reset</Button>
              </Grid>

              
            </Grid>
          </GlassCard>

            {/* Trend */}
            <GlassCard sx={{ p: 3, mb: 2 }}>
              <SectionHeader
                title={`Trend: Created (${trendView}) vs last`}
                right={
                  <Stack direction="row" spacing={1}>
                    {(["day","week","month","year"]).map(v => (
                      <Chip
                        key={v}
                        label={v}
                        size="small"
                        variant={trendView===v?"filled":"outlined"}
                        onClick={()=>setTrendView(v)}
                        sx={{ textTransform: "capitalize" }}
                      />
                    ))}
                  </Stack>
                }
              />
              {loading ? (
                <Skeleton variant="rounded" height={320} />
              ) : workOrders.length === 0 ? (
                <EmptyState />
              ) : (
                <Box sx={{ height: 360 }}>
                  <Line
                    data={comparisonChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: basePlugins,
                      scales: { x: { grid: { display: false }}, y: { grid: { color: "rgba(0,0,0,0.06)" } } },
                    }}
                  />
                </Box>
              )}
            </GlassCard>

            {/* Types vs last + Status pie */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={8}>
                <GlassCard sx={{ height: "100%" }}>
                  <SectionHeader title={`Work Orders by Type (this vs last)`} />
                  {loading ? (
                    <Skeleton variant="rounded" height={320} />
                  ) : workOrders.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <Box sx={{ height: 360 }}>
                      <Bar
                        data={typeComparisonChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: basePlugins,
                          scales: { x: { grid: { display: false }}, y: { grid: { color: "rgba(0,0,0,0.06)" } } },
                        }}
                      />
                    </Box>
                  )}
                </GlassCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <GlassCard sx={{ height: "100%" }}>
                  <SectionHeader title="By Status" />
                  {loading ? (
                    <Skeleton variant="rounded" height={280} />
                  ) : workOrders.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <Box sx={{ display:"flex", justifyContent:"center", alignItems:"center", height: 300 }}>
                     <Pie
  data={statusChartData}
  options={{
    plugins: {
      ...basePlugins,
      legend: { position: "bottom" },
      datalabels: {
        clip: true,
offset: 2,
display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
        color: "#111",
        font: { weight: "bold" },
        formatter: (value, ctx) => {
          const data = ctx.chart.data.datasets[0].data || [];
          const total = data.reduce((a, b) => a + b, 0);
          const pct = total ? Math.round((value / total) * 100) : 0;
          return `${value} (${pct}%)`;
        },
        padding: 4,
        borderRadius: 6,
        backgroundColor: "rgba(255,255,255,0.8)",
      },
    },
    maintainAspectRatio: false,
  }}
/>

                    </Box>
                  )}
                </GlassCard>
              </Grid>
            </Grid>

            {/* Membership type */}
            <GlassCard sx={{ mb: 2 }}>
              <SectionHeader title="By ClubMembershipTypeCode" />
              {!includeMember && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Toggle "Include member info" in filters to enable this chart.
                </Typography>
              )}
              <Box sx={{ height: 360 }}>
                <Bar
                  data={membershipChartData}
                  options={{
                    indexAxis: "y",
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: basePlugins,
                    scales: { x: { grid: { color: "rgba(0,0,0,0.06)" }}, y: { grid: { display: false } } },
                  }}
                />
              </Box>
            </GlassCard>

            {/* Priority */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <GlassCard>
                  <SectionHeader title="By Priority" />
                  {loading ? (
                    <Skeleton variant="rounded" height={280} />
                  ) : workOrders.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <Box sx={{ display:"flex", justifyContent:"center", alignItems:"center", height: 300 }}>
                      <Pie
  data={priorityChartData}
  options={{
    plugins: {
      ...basePlugins,
      legend: { position: "bottom" },
      datalabels: {
        clip: true,
offset: 2,
display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
        color: "#111",
        font: { weight: "bold" },
        formatter: (value, ctx) => {
          const data = ctx.chart.data.datasets[0].data || [];
          const total = data.reduce((a, b) => a + b, 0);
          const pct = total ? Math.round((value / total) * 100) : 0;
          return `${value} (${pct}%)`;
        },
        padding: 4,
        borderRadius: 6,
        backgroundColor: "rgba(255,255,255,0.8)",
      },
    },
    maintainAspectRatio: false,
  }}
/>
                    </Box>
                  )}
                </GlassCard>
              </Grid>
            </Grid>
          </Box>

         
        </>
      )}

      {activeTab === 1 && <ExportTab />}
    </Box>
  );
};

export default WorkOrdersTab;
